"use client";

import { PaginationControls } from "@/components/ui/pagination";
import { ListSkeleton } from "@/components/shared";
import { PublisherTopSellersList } from "./PublisherTopSellersList";
import { useListPagination } from "@/hooks";
import type { PublisherTopSellerGame } from "../types";
import { publisherTopSellersSkeletonConfig } from "../config";

interface PublisherTopSellersListWithPaginationProps {
  initialGames: PublisherTopSellerGame[];
  initialPage: number;
  initialTotal: number;
  pageSize: number;
  clanid: number;
  type?: string | null;
  comingSoon?: boolean;
  cc?: string;
}

async function loadPublisherTopSellers(
  clanid: number,
  page: number,
  pageSize: number,
  type?: string | null,
  comingSoon?: boolean,
  cc?: string,
): Promise<{ items: PublisherTopSellerGame[]; total: number }> {
  const { getPublisherTopSellersClient } =
    await import("../api/getPublisherTopSellersClient");
  const offset = (page - 1) * pageSize;
  const { games, total } = await getPublisherTopSellersClient(
    clanid,
    pageSize,
    offset,
    type,
    comingSoon,
    cc,
  );
  return { items: games, total };
}

export function PublisherTopSellersListWithPagination({
  initialGames,
  initialPage,
  initialTotal,
  pageSize,
  clanid,
  type,
  comingSoon,
  cc,
}: PublisherTopSellersListWithPaginationProps) {
  const {
    items: games,
    currentPage,
    pageSize: currentPageSize,
    totalPages,
    isPending,
    handlePageChange,
    handlePageSizeChange,
  } = useListPagination({
    initialItems: initialGames,
    initialPage,
    initialTotal,
    pageSize,
    sortable: false,
    onPageChange: (page, pageSize) =>
      loadPublisherTopSellers(clanid, page, pageSize, type, comingSoon, cc),
  });

  return (
    <div className="space-y-8">
      {isPending ? (
        <ListSkeleton columns={publisherTopSellersSkeletonConfig} />
      ) : (
        <PublisherTopSellersList games={games} />
      )}

      <PaginationControls
        currentPage={currentPage}
        totalPages={totalPages}
        onPageChange={handlePageChange}
        pageSize={currentPageSize}
        onPageSizeChange={handlePageSizeChange!}
        disabled={isPending}
      />
    </div>
  );
}
